<template>
  <div class="text-center items-center p-3 flex flex-col justify-center w-screen h-screen">
    <div class="font-bold text-primary-0 space-x-2 text-2xl">
      <span>TEST PAGE</span>
      <template v-if="!isLoading">
        <span>-</span>
        <span
          v-if="isLoggedIn"
          class="text-secondary--1"
          >Logged in</span
        >
        <span
          v-else
          class="text-alert-0"
          >Logged out</span
        >
      </template>
    </div>

    <div v-if="isLoading">
      <div class="text-grey-0 text-center text-lg">Loading...</div>

      <Spinner
        fill="#6E41BF"
        class="mx-auto fill-primary-0 text-primary-0"
      />
    </div>

    <template v-else>
      <hr class="border-grey-3 my-4 w-full" />

      <pre>
     <div>
       isInstalled: {{ isInstalled }}
       isLoading: {{ isLoading }}
       isLoggedIn: {{ isLoggedIn }}
       hasError: {{ hasError }}
       errorMessage: {{ errorMessage }}
     </div>
    </pre>

      <div
        v-if="hasError"
        class="p-3 shadow-sm rounded-sm text-sm w-full bg-alert-0 text-white my-2 text-center"
      >
        {{ errorMessage }}
      </div>

      <BaseButton
        class="mt-2"
        :disabled="isLoggedIn"
        @click="tryHandleLogin"
      >
        Handle login
      </BaseButton>

      <BaseButton
        class="mt-2"
        :loading="singOutLoading"
        :disabled="!isLoggedIn"
        @click="tryLogOut"
      >
        Logout
      </BaseButton>

      <BaseButton
        class="mt-2"
        :disabled="!isLoggedIn"
        @click="tryRefreshTokens"
      >
        Refresh Tokens
      </BaseButton>

      <BaseButton
        class="mt-2"
        :loading="loadingUserInfo"
        :disabled="!isLoggedIn"
        @click="loadUserInfo"
      >
        Load user info
      </BaseButton>

      <template v-if="isLoggedIn && hasUserInfo">
        <hr class="border-grey-3 my-4 w-full" />

        <div class="text-center text-accent--1 font-bold">User info:</div>

        <pre>
          {{ userInfoDataState }}
        </pre>
      </template>

      <template v-if="!isLoggedIn">
        <hr class="mt-4 mb-2 border-grey-3 w-full" />
        <BaseButton
          class="mt-2"
          :outline="true"
          @click="goToLogin"
        >
          Go to login page
        </BaseButton>
      </template>
    </template>
  </div>
</template>

<script setup>
import { computed, inject, ref } from 'vue'
import { useRouter } from 'vue-router'
import { AUTHENTICATOR_KEY } from '@/services/auth/Plugin'
import BaseButton from '@/components/buttons/BaseButton.vue'
import { useFetch } from '@vueuse/core'
import { ROUTE_NAMES } from '@/router'
import Spinner from '@/components/loading/Spinner.vue'

const router = useRouter()

const Authenticator = inject(AUTHENTICATOR_KEY)

const { isInstalled, isLoggedIn, isLoading, hasError, errorMessage } = Authenticator.useState()

const { handleLogin, refreshTokens, handleLogout } = Authenticator.useActions()

const tryHandleLogin = () => {
  handleLogin()
}

const tryRefreshTokens = () => {
  refreshTokens()
}

const loadingUserInfo = ref(false)
const userInfoDataState = ref({})
const hasUserInfo = computed(() => Object.keys(userInfoDataState.value).length > 0)
const loadUserInfo = async () => {
  loadingUserInfo.value = true
  const accessToken = Authenticator.tokens.getAccessToken()

  // then get user info
  const { data: userInfoData, error: userInfoError } = await useFetch(
    `${import.meta.env.VITE_APP_API_URL}/v3/users/user-info`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      }
    }
  ).json()

  if (!userInfoError.value) {
    userInfoDataState.value = userInfoData.value.user
  }

  loadingUserInfo.value = false
}

const singOutLoading = ref(false)
const tryLogOut = async () => {
  singOutLoading.value = true
  await handleLogout()
  singOutLoading.value = false
  userInfoDataState.value = {}
  await router.replace({ name: ROUTE_NAMES.TEST })
}

const goToLogin = () => router.push({ name: ROUTE_NAMES.LOGIN })
</script>
